import React, { useMemo, useState } from "react";
import { Autocomplete, type AutocompleteProps } from "@mui/material";
import { TextField } from "@mui/material";
import type { TextFieldProps } from "@mui/material";
import { shouldBlockUnsafeSearchInput } from "@/helpers/sanitizeHelper";

const AutocompleteComponent = Autocomplete as React.ElementType;

type AutocompleteOption = {
  label?: string;
  value?: string | number;
};

export default function FormikMUIAutocompleteSelectInput({
  label,
  placeholder = "",
  className = "",
  options = [],
  size = "small",
  variant = "outlined",
  multiple = false,
  staticLabel = false,
  field,
  form: { touched, errors, setFieldValue },
  ...rest
}: {
  label?: React.ReactNode;
  placeholder?: string;
  className?: string;
  options?: AutocompleteOption[];
  size?: AutocompleteProps<
    AutocompleteOption,
    boolean,
    boolean,
    boolean
  >["size"];
  variant?: TextFieldProps["variant"];
  multiple?: boolean;
  staticLabel?: boolean;
  field: {
    name: string;
    value?: string | number | Array<string | number>;
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
  };
  form: {
    touched: Record<string, boolean | undefined>;
    errors: Record<string, React.ReactNode>;
    setFieldValue: (_name: string, _value: unknown) => void;
  };
  [key: string]: any;
}) {
  const { onInputChange: externalOnInputChange, ...autocompleteProps } = rest;
  const [inputValue, setInputValue] = useState("");

  const selectedValue = useMemo(() => {
    if (multiple) {
      const currentValue = Array.isArray(field.value) ? field.value : [];

      return options?.filter((o) => currentValue.includes(o.value || "")) || [];
    }

    return options?.find((o) => o.value === field.value) || "";
  }, [field.value, options, multiple]);

  const err =
    touched[field.name] && errors[field.name] ? errors[field.name] : "";

  return (
    <AutocompleteComponent
      className={className}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "input" && shouldBlockUnsafeSearchInput(newInputValue)) {
          return;
        }

        setInputValue(newInputValue);
        externalOnInputChange?.(event, newInputValue, reason);
      }}
      options={options}
      getOptionLabel={(option) => {
        return option?.label || "";
      }}
      disableClearable
      openOnFocus
      autoHighlight
      autoComplete
      size={size}
      {...autocompleteProps}
      multiple={multiple}
      onBlur={field.onBlur}
      id={field.name}
      name={field.name}
      value={selectedValue}
      onChange={(event, newValue) => {
        if (multiple) {
          if (!newValue) {
            setFieldValue(field.name, []);
            return;
          } else if (Array.isArray(newValue)) {
            setFieldValue(
              field.name,
              newValue.map((o) => o.value)
            );
            return;
          }
        } else {
          if (!newValue) {
            setFieldValue(field.name, "");
            return;
          } else if (typeof newValue === "object") {
            setFieldValue(field.name, newValue.value);
            return;
          }
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant={variant}
          margin="dense"
          helperText={err}
          error={err ? true : false}
          placeholder={placeholder}
          slotProps={{
            inputLabel: staticLabel ? { shrink: true } : undefined,
            htmlInput: {
              ...params.inputProps,
              autoComplete: "new-password",
            },
          }}
        />
      )}
    />
  );
}
